import { Trans } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { isMissingCustomsData } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import {
  ShipmentWithRelations,
  useArticleOngoingShipment,
  useArticleShipmentWithIssue,
  useCarrierName,
} from '@/models/shipment';
import { hasPermission } from '@/models/user';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useCurrentSession } from '@/services/auth';
import { formatDate } from '@/utils/date';

import { RecordClientContactMessage } from './shared/RecordClientContactMessage';
import { BaseMessage } from './BaseMessage';
import { GoToShipment } from './GoToShipment';

export const TransitStepMessage = ({ article }: { article: ArticleWithRelations }) => {
  const { request, view: requestView } = useRequestContext();

  const { shipment, isLoading: isLoadingShipment } = useArticleOngoingShipment(article);
  const { shipment: shipmentWithIssue, isLoading: isLoadingShipmentWithIssue } =
    useArticleShipmentWithIssue(article);

  if (isLoadingShipment || isLoadingShipmentWithIssue) {
    return null;
  }

  if (
    !!request.storeId &&
    article.atEntityId === request.storeId &&
    article.toClient &&
    article.step?.step === 'transit' &&
    article.step.config.clientPickup
  ) {
    return <ClientPickupInStore article={article} />;
  }

  if (article.selfHandledTransit?.started) {
    return <SelfHandledTransitVerification article={article} />;
  }

  if (!shipment && !shipmentWithIssue) {
    if (
      article.areCustomsExpected &&
      (article.unhiddenTaskType === 'add_customs_informations' ||
        isMissingCustomsData(article, requestView.customs.type))
    ) {
      return <MissingCustoms article={article} />;
    }

    return <ShipmentInPreparation article={article} />;
  }

  if (shipment && shipment.step === 'preparation') {
    return <ShipmentInPreparation article={article} />;
  }

  if (shipment && shipment.step === 'finalisation') {
    return <ShipmentInFinalisation article={article} />;
  }

  if (shipment && shipment.step === 'handover' && shipment.status === 'pending-pickup') {
    return <ShipmentPendingPickup article={article} shipment={shipment} />;
  }

  if (shipment && shipment.step === 'handover' && shipment.status === 'pending-dropoff') {
    return <ShipmentPendingDropoff article={article} shipment={shipment} />;
  }

  if (shipment && shipment.step === 'in-transit') {
    return <ShipmentInTransit article={article} />;
  }

  if (shipment && shipment.step === 'verification') {
    return <ShipmentReceived article={article} />;
  }

  if (shipmentWithIssue && shipmentWithIssue.step === 'validated' && article.hasIssue) {
    return <ShipmentIssue article={article} shipment={shipmentWithIssue} />;
  }

  return null;
};

const ClientPickupInStore = ({ article }: { article: ArticleWithRelations }) => {
  const { request } = useRequestContext();
  const { currentSession } = useCurrentSession();

  const contactClientTaskEnabled =
    article.step?.step === 'transit' && article.step.config.tasks?.contact_client?.enabled;
  const canRecordClientContact = hasPermission(currentSession, 'write_client_communication', {
    entityId: request.storeId,
  });

  const isClientCommunicationEnabled =
    request.organization.config.clientCommunication?.notifications?.enabled;

  return (
    <BaseMessage
      title={
        <Trans id="article.step-message.transit.store-client.title">
          Waiting for client pickup
        </Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <p>
            {isClientCommunicationEnabled ? (
              <Trans id="article.step-message.transit.store-client.text">
                The package is at the store and the client has been notified that they can pick up
                the item.
              </Trans>
            ) : (
              <Trans id="article.step-message.transit.store-client.text.no-communication">
                The item is ready to be picked up by the client.
              </Trans>
            )}
          </p>
          {contactClientTaskEnabled && canRecordClientContact && <RecordClientContactMessage />}
        </Stack>
      }
      article={article}
    />
  );
};

const MissingCustoms = ({ article }: { article: ArticleWithRelations }) => {
  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.customs.title">
          Waiting for customs information
        </Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <Paragraph>
            <Trans id="article.step-message.transit.customs.message">
              The item cannot be shipped without customs information due to the destination country.
              The responsible users have been notified.
            </Trans>
          </Paragraph>
        </Stack>
      }
    />
  );
};

const ShipmentInPreparation = ({ article }: { article: ArticleWithRelations }) => {
  const originName = article.atEntity?.name;
  const destinationName = article.toEntity?.name;

  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.preparation.title">
          Waiting for package preparation
        </Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <>
            {!!originName && !!destinationName && (
              <p>
                <Trans id="article.step-message.transit.preparation.text.entity-to-entity">
                  {originName} is preparing the package for delivery to {destinationName}.
                </Trans>
              </p>
            )}
            {!!originName && !!article.toClient && (
              <p>
                <Trans id="article.step-message.transit.preparation.text.entity-to-client">
                  {originName} is preparing the package for delivery to the client.
                </Trans>
              </p>
            )}
          </>
          <GoToShipment article={article} />
        </Stack>
      }
    />
  );
};

const ShipmentInFinalisation = ({ article }: { article: ArticleWithRelations }) => {
  const originName = article.atEntity?.name;
  const destinationName = article.toEntity?.name;

  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.finalisation.title">
          Waiting for package finalisation
        </Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <>
            {!!originName && !!destinationName && (
              <p>
                <Trans id="article.step-message.transit.finalisation.text.entity-to-entity">
                  {originName} is finishing the package for delivery to {destinationName}.
                </Trans>
              </p>
            )}
            {!!originName && article.toClient && (
              <p>
                <Trans id="article.step-message.transit.finalisation.text.entity-to-client">
                  {originName} is finishing the package for delivery to the client.
                </Trans>
              </p>
            )}
          </>
          <GoToShipment article={article} />
        </Stack>
      }
    />
  );
};

const ShipmentPendingPickup = ({
  article,
  shipment,
}: {
  article: ArticleWithRelations;
  shipment: ShipmentWithRelations;
}) => {
  const pickupDate = shipment.pickupDate
    ? formatDate(parseISO(shipment.pickupDate), {})
    : undefined;

  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.pickup.title">Waiting for carrier pickup</Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          {shipment.handover === 'automatic_pickup' && (
            <p>
              <Trans id="article.step-message.transit.automatic-pickup.text">
                The carrier will pick up the package at the next pre-agreed slot.
              </Trans>
            </p>
          )}
          {shipment.handover === 'pickup' && !!pickupDate && (
            <p>
              <Trans id="article.step-message.transit.pickup.text">
                The pickup is scheduled for {pickupDate}
              </Trans>
            </p>
          )}
          <GoToShipment article={article} />
        </Stack>
      }
    />
  );
};

const ShipmentPendingDropoff = ({
  article,
  shipment,
}: {
  article: ArticleWithRelations;
  shipment: ShipmentWithRelations;
}) => {
  const carrierName = useCarrierName(shipment);

  const originName = article.atEntity?.name;

  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.dropoff.title">Waiting for carrier drop-off</Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          {!!carrierName && (
            <>
              {!!originName && (
                <p>
                  <Trans id="article.step-message.transit.dropoff.text.entity">
                    {originName} will drop the package off at the nearest {carrierName} pickup
                    point.
                  </Trans>
                </p>
              )}
              {article.atClient && (
                <p>
                  <Trans id="article.step-message.transit.dropoff.text.client">
                    The client will drop the package off at the nearest {carrierName} pickup point.
                  </Trans>
                </p>
              )}
            </>
          )}
          <GoToShipment article={article} />
        </Stack>
      }
    />
  );
};

const ShipmentInTransit = ({ article }: { article: ArticleWithRelations }) => {
  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.transit.title">The delivery is in progress</Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <p>
            <Trans id="article.step-message.transit.transit.text">
              The carrier has taken charge of the package.
            </Trans>
          </p>
          <GoToShipment article={article} />
        </Stack>
      }
    />
  );
};

const ShipmentReceived = ({ article }: { article: ArticleWithRelations }) => {
  const destinationName = article.toEntity?.name;

  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.received.title">
          The delivery is undergoing verification
        </Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <>
            {!!destinationName && (
              <p>
                <Trans id="article.step-message.transit.received.text">
                  The package has arrived at its destination. Its contents are currently being
                  checked.
                </Trans>
              </p>
            )}
          </>
          <GoToShipment article={article} />
        </Stack>
      }
    />
  );
};

const ShipmentIssue = ({
  article,
  shipment,
}: {
  article: ArticleWithRelations;
  shipment: ShipmentWithRelations;
}) => {
  const issue = shipment.articles.find(({ articleId }) => articleId === article.id)?.issue;

  return (
    <BaseMessage
      title={
        <Trans id="article.step-message.transit.issue.title">
          A shipment issue has been reported
        </Trans>
      }
      message={<>{!!issue && <p>&quot;{issue}&quot;</p>}</>}
      article={article}
    />
  );
};

const SelfHandledTransitVerification = ({ article }: { article: ArticleWithRelations }) => {
  const { currentSession } = useCurrentSession();
  const destinationName = article.toEntity?.name;

  const canVerifyReception = hasPermission(currentSession, 'verify_shipment_reception', [
    {
      entityId: article.toEntityId,
    },
  ]);

  return (
    <BaseMessage
      article={article}
      title={
        <Trans id="article.step-message.transit.self-handled-verification.title">
          Waiting for package verification
        </Trans>
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          <p>
            {canVerifyReception ? (
              <Trans id="article.step-message.transit.self-handled-verification.text">
                The package is in transit. Once the package arrives, please confirm that you have
                received it.
              </Trans>
            ) : (
              <Trans id="article.step-message.transit.self-handled-verification.text.store">
                The package is on its way to {destinationName} and is waiting to be verified.
              </Trans>
            )}
          </p>
        </Stack>
      }
    />
  );
};
